import React, { useState, useEffect } from 'react';
import { Search, Save, X, Edit2, Check, ArrowUp, ArrowDown, Trash2, RefreshCw, Loader2, Flag, Award } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ülke bayrakları
const COUNTRY_FLAGS = {
  TR: { flag: '🇹🇷', name: 'Türkiye' },
  DE: { flag: '🇩🇪', name: 'Almanya' },
  US: { flag: '🇺🇸', name: 'ABD' },
  GB: { flag: '🇬🇧', name: 'İngiltere' },
  FR: { flag: '🇫🇷', name: 'Fransa' },
  IT: { flag: '🇮🇹', name: 'İtalya' },
  ES: { flag: '🇪🇸', name: 'İspanya' },
  CN: { flag: '🇨🇳', name: 'Çin' },
  JP: { flag: '🇯🇵', name: 'Japonya' },
  KR: { flag: '🇰🇷', name: 'Güney Kore' },
};

const EnhancedProductOrderingTab = ({
  settings,
  updateSetting,
  selectedFlashDeals,
  setSelectedFlashDeals,
  selectedRecommended,
  setSelectedRecommended,
  setHasChanges
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('flash-deals');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [currentProducts, setCurrentProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [showFlagMenu, setShowFlagMenu] = useState(null);

  // Ürüne bayrak ekle/kaldır
  const toggleFlag = (productId, countryCode) => {
    const updateList = (list, setList) => {
      setList(list.map(p => {
        if (p.id === productId) {
          const currentFlag = p.country_flag;
          return { 
            ...p, 
            country_flag: currentFlag === countryCode ? null : countryCode 
          };
        }
        return p;
      }));
    };
    
    if (activeTab === 'flash-deals') {
      updateList(selectedFlashDeals, setSelectedFlashDeals);
    } else {
      updateList(selectedRecommended, setSelectedRecommended);
    }
    setHasChanges(true);
    setShowFlagMenu(null);
  };

  // Made in Turkey toggle
  const toggleMadeInTurkey = (productId) => {
    const updateList = (list, setList) => {
      setList(list.map(p => {
        if (p.id === productId) {
          return { ...p, made_in_turkey: !p.made_in_turkey };
        }
        return p;
      }));
    };
    
    if (activeTab === 'flash-deals') {
      updateList(selectedFlashDeals, setSelectedFlashDeals);
    } else {
      updateList(selectedRecommended, setSelectedRecommended);
    }
    setHasChanges(true);
  };

  // Mevcut ana sayfa ürünlerini yükle
  const fetchCurrentProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await api.get('/products/mixed-by-category', {
        params: { products_per_category: 4, total_products: 32, offset: activeTab === 'flash-deals' ? 0 : 50 }
      });
      setCurrentProducts(res.data.products || []);
    } catch (error) {
      console.error('Products fetch error:', error);
      toast.error('Ürünler yüklenemedi');
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchCurrentProducts();
  }, [activeTab]);

  // Ürün ara
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await api.get('/admin/products/search', {
        params: { q: query, limit: 20 }
      });
      setSearchResults(res.data.products || []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Arama başarısız');
    } finally {
      setSearching(false);
    }
  };

  // Ürün ekle
  const addProduct = (product) => {
    if (activeTab === 'flash-deals') {
      if (!selectedFlashDeals.find(p => p.id === product.id)) {
        setSelectedFlashDeals([...selectedFlashDeals, product]);
        setHasChanges(true);
        toast.success('Ürün Kaçırılmayacak Fırsatlar\'a eklendi');
      }
    } else {
      if (!selectedRecommended.find(p => p.id === product.id)) {
        setSelectedRecommended([...selectedRecommended, product]);
        setHasChanges(true);
        toast.success('Ürün Sizin İçin Önerilen\'e eklendi');
      }
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  // Ürün kaldır
  const removeProduct = (productId) => {
    if (activeTab === 'flash-deals') {
      setSelectedFlashDeals(selectedFlashDeals.filter(p => p.id !== productId));
    } else {
      setSelectedRecommended(selectedRecommended.filter(p => p.id !== productId));
    }
    setHasChanges(true);
  };

  // Sıra değiştir
  const moveProduct = (index, direction) => {
    const products = activeTab === 'flash-deals' ? [...selectedFlashDeals] : [...selectedRecommended];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= products.length) return;
    
    [products[index], products[newIndex]] = [products[newIndex], products[index]];
    
    if (activeTab === 'flash-deals') {
      setSelectedFlashDeals(products);
    } else {
      setSelectedRecommended(products);
    }
    setHasChanges(true);
  };

  // Fiyat düzenleme başlat
  const startEditPrice = (product) => {
    setEditingPrice(product.id);
    setNewPrice(product.price.toString());
  };

  // Fiyat kaydet
  const savePrice = async (product) => {
    if (!newPrice || isNaN(parseFloat(newPrice))) {
      toast.error('Geçerli bir fiyat girin');
      return;
    }

    setSavingPrice(true);
    try {
      await api.put(`/admin/products/${product.id}/price`, {
        price: parseFloat(newPrice)
      });
      
      // Listeyi güncelle
      const updateList = (list, setList) => {
        setList(list.map(p => p.id === product.id ? { ...p, price: parseFloat(newPrice) } : p));
      };
      
      updateList(selectedFlashDeals, setSelectedFlashDeals);
      updateList(selectedRecommended, setSelectedRecommended);
      updateList(currentProducts, setCurrentProducts);
      
      setEditingPrice(null);
      setNewPrice('');
      toast.success('Fiyat güncellendi');
    } catch (error) {
      console.error('Price update error:', error);
      toast.error('Fiyat güncellenemedi');
    } finally {
      setSavingPrice(false);
    }
  };

  const selectedProducts = activeTab === 'flash-deals' ? selectedFlashDeals : selectedRecommended;
  const autoSelect = activeTab === 'flash-deals' 
    ? settings.flash_deals_auto_select !== false 
    : settings.recommended_auto_select !== false;

  // eslint-disable-next-line react/no-unstable-nested-components
  const ProductCard = ({ product, index, showOrder = false, showEdit = true }) => (
    <div className="relative border rounded-lg p-2 bg-white group hover:shadow-md transition-shadow">
      {showOrder && (
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold z-10">
          {index + 1}
        </div>
      )}
      
      {showEdit && (
        <button
          onClick={() => removeProduct(product.id)}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Bayrak ve Made in Turkey göstergeleri */}
      <div className="absolute top-1 left-1 flex flex-col gap-1 z-10">
        {product.country_flag && (
          <span className="text-lg drop-shadow" title={COUNTRY_FLAGS[product.country_flag]?.name}>
            {COUNTRY_FLAGS[product.country_flag]?.flag}
          </span>
        )}
        {product.made_in_turkey && (
          <img 
            src="https://customer-assets.emergentagent.com/job_emarket-turkey-1/artifacts/dupde9ke_madeinturkiye%20logo%20.png"
            alt="Made in Türkiye"
            className="w-6 h-6 object-contain drop-shadow"
          />
        )}
      </div>
      
      <img 
        src={product.images?.[0] || 'https://via.placeholder.com/100'} 
        alt={product.name}
        className="w-full aspect-square object-cover rounded mb-2"
        onError={(e) => e.target.src = 'https://via.placeholder.com/100?text=No+Image'}
      />
      
      <p className="text-xs font-medium truncate" title={product.name}>{product.name}</p>
      <p className="text-xs text-gray-500">{product.category}</p>
      
      {/* Fiyat düzenleme */}
      {editingPrice === product.id ? (
        <div className="flex items-center gap-1 mt-1">
          <Input
            type="number"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            className="h-6 text-xs w-16 p-1"
            autoFocus
          />
          <span className="text-xs">₺</span>
          <button 
            onClick={() => savePrice(product)}
            disabled={savingPrice}
            className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
          >
            {savingPrice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
          <button 
            onClick={() => { setEditingPrice(null); setNewPrice(''); }}
            className="p-1 bg-gray-400 text-white rounded hover:bg-gray-500"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs font-bold text-green-600">{product.price?.toFixed(2)} ₺</span>
          {showEdit && (
            <button 
              onClick={() => startEditPrice(product)}
              className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Fiyat düzenle"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
      
      {/* Bayrak ve Made in Turkey butonları */}
      {showEdit && showOrder && (
        <div className="flex flex-wrap justify-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Bayrak Menüsü */}
          <div className="relative">
            <button
              onClick={() => setShowFlagMenu(showFlagMenu === product.id ? null : product.id)}
              className={`p-1 rounded text-xs ${product.country_flag ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'} hover:bg-blue-200`}
              title="Bayrak ekle/kaldır"
            >
              <Flag className="w-3 h-3" />
            </button>
            {showFlagMenu === product.id && (
              <div className="absolute bottom-full left-0 mb-1 bg-white border rounded-lg shadow-lg p-2 z-50 min-w-[120px]">
                <div className="text-xs font-medium mb-1 text-gray-500">Bayrak Seç:</div>
                <div className="grid grid-cols-5 gap-1">
                  {Object.entries(COUNTRY_FLAGS).map(([code, { flag, name }]) => (
                    <button
                      key={code}
                      onClick={() => toggleFlag(product.id, code)}
                      className={`text-lg p-1 rounded hover:bg-gray-100 ${product.country_flag === code ? 'bg-blue-100 ring-2 ring-blue-400' : ''}`}
                      title={name}
                    >
                      {flag}
                    </button>
                  ))}
                </div>
                {product.country_flag && (
                  <button
                    onClick={() => toggleFlag(product.id, product.country_flag)}
                    className="w-full mt-2 text-xs text-red-500 hover:bg-red-50 p-1 rounded"
                  >
                    Bayrağı Kaldır
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Made in Turkey Toggle */}
          <button
            onClick={() => toggleMadeInTurkey(product.id)}
            className={`p-1 rounded text-xs ${product.made_in_turkey ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'} hover:bg-red-200`}
            title={product.made_in_turkey ? 'Made in Turkey kaldır' : 'Made in Turkey ekle'}
          >
            <Award className="w-3 h-3" />
          </button>
          
          {/* Sıralama butonları */}
          <button
            onClick={() => moveProduct(index, -1)}
            disabled={index === 0}
            className="p-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            <ArrowUp className="w-3 h-3" />
          </button>
          <button
            onClick={() => moveProduct(index, 1)}
            disabled={index === selectedProducts.length - 1}
            className="p-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ürün Sıralaması ve Fiyat Yönetimi</CardTitle>
        <CardDescription>Ana sayfadaki ürünleri yönetin, sıralarını değiştirin ve fiyatlarını güncelleyin</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="flash-deals">
              Kaçırılmayacak Fırsatlar
              <Badge variant="secondary" className="ml-2">{selectedFlashDeals.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="recommended">
              Sizin İçin Önerilen
              <Badge variant="secondary" className="ml-2">{selectedRecommended.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-6">
            {/* Otomatik Seçim Switch */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Otomatik Ürün Seçimi</Label>
                <p className="text-sm text-gray-500">Kapatırsanız sadece manuel eklenen ürünler gösterilir</p>
              </div>
              <Switch
                checked={autoSelect}
                onCheckedChange={(v) => {
                  if (activeTab === 'flash-deals') {
                    updateSetting('flash_deals_auto_select', v);
                  } else {
                    updateSetting('recommended_auto_select', v);
                  }
                }}
              />
            </div>

            {/* Ürün Arama */}
            <div className="space-y-2">
              <Label>Ürün Ara ve Ekle</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Ürün adı, kategori veya SKU ile arayın..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                )}
                
                {/* Arama Sonuçları */}
                {searchResults.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-80 overflow-y-auto">
                    {searchResults.map((product) => (
                      <button
                        key={product.id}
                        className={`w-full p-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b last:border-b-0 ${
                          selectedProducts.find(p => p.id === product.id) ? 'bg-green-50' : ''
                        }`}
                        onClick={() => addProduct(product)}
                      >
                        <img 
                          src={product.images?.[0] || 'https://via.placeholder.com/40'} 
                          alt={product.name}
                          className="w-12 h-12 rounded object-cover"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/40?text=N'}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.category} • {product.sku}</p>
                          <p className="text-xs font-bold text-green-600">{product.price?.toFixed(2)} ₺</p>
                        </div>
                        {selectedProducts.find(p => p.id === product.id) && (
                          <Check className="w-5 h-5 text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Manuel Seçili Ürünler */}
            {selectedProducts.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Manuel Seçili Ürünler ({selectedProducts.length})</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (activeTab === 'flash-deals') {
                        setSelectedFlashDeals([]);
                      } else {
                        setSelectedRecommended([]);
                      }
                      setHasChanges(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Tümünü Temizle
                  </Button>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                  {selectedProducts.map((product, index) => (
                    <ProductCard 
                      key={product.id} 
                      product={product} 
                      index={index} 
                      showOrder={true}
                      showEdit={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Otomatik Seçilen Ürünler (Mevcut Ana Sayfa) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Şu Anda Ana Sayfada Gösterilen Ürünler</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchCurrentProducts}
                  disabled={loadingProducts}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingProducts ? 'animate-spin' : ''}`} />
                  Yenile
                </Button>
              </div>
              
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Ürünler yükleniyor...</span>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                  {currentProducts.map((product, index) => (
                    <div key={product.id} className="relative">
                      <ProductCard 
                        product={product} 
                        index={index} 
                        showOrder={false}
                        showEdit={false}
                      />
                      {!selectedProducts.find(p => p.id === product.id) && (
                        <button
                          onClick={() => addProduct(product)}
                          className="absolute bottom-1 right-1 p-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          title="Listeye Ekle"
                        >
                          +
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default EnhancedProductOrderingTab;
